import { isDefined } from '@sgme/fp';
import { createSelector } from 'reselect';
import type { RoutePath } from 'state/router';
import type { AppState } from 'state/state.model';
import { getUserPreferencesLandingPage } from 'state/userPreferences/userPreferences.selectors';

import { getIsUserInternal } from './user';
import { getUserFrontRights, isFeatureEnabled } from './userFeatures';

function isPrimaryQuoteEnabled(state: AppState) {
  return getUserFrontRights(state).quoteEnabled;
}

export function isSmartQuoteWorkspaceVisible(state: AppState) {
  return getUserFrontRights(state).smartQuoteEnabled;
}

export function isEssentialWorkspaceVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state) && isFeatureEnabled(state, 'essentialWorkspaceParis');
}

export function isAdvancedWorkspaceVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state);
}

export function isQuoteBlotterVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state);
}

export function isSecondaryQuoteEnabled(state: AppState) {
  return getUserFrontRights(state).secondaryQuoteEnabled;
}
export function isSecondaryBatchVisible(state: AppState) {
  return isSecondaryQuoteEnabled(state) && isFeatureEnabled(state, 'batchSecondaryParis');
}

export function isSecondaryTradeBlotterVisible(state: AppState) {
  return isSecondaryQuoteEnabled(state);
}

export function isSecondaryOrderBlotterVisible(state: AppState) {
  return isSecondaryQuoteEnabled(state) && getIsUserInternal(state);
}

export function isSecondaryPostEventVisible(state: AppState) {
  return isSecondaryQuoteEnabled(state) && getIsUserInternal(state);
}

export function isTradeBlotterVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state);
}
export function isPriceImprovementBlotterVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state) && getIsUserInternal(state);
}
export function isDynamicWorkspaceVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state) && getIsUserInternal(state);
}
export function isScheduledQuotesBlotterVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state) && isFeatureEnabled(state, 'scheduledRunsParis');
}

export function isPostTradeVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state) && getIsUserInternal(state);
}

export function isBatchWorkspaceVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state);
}

export function isDiscoverPageVisible(state: AppState) {
  return getUserFrontRights(state).discoverPageEnabled;
}

export function isAddonManagementVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state) && getIsUserInternal(state);
}

export function isSPLabVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state) && getIsUserInternal(state);
}

export function isUnderlyingUniverseVisible(state: AppState) {
  return isPrimaryQuoteEnabled(state);
}

export function getDefaultRoutePath(state: AppState): RoutePath {
  if (isFeatureEnabled(state, 'essentialWorkspaceParis')) {
    return getDefaultRoutePathNew(state);
  }
  return getDefaultRoutePathOld(state);
}

const getDefaultRoutePathNew: (state: AppState) => RoutePath = createSelector(
  [
    getUserPreferencesLandingPage,
    isAdvancedWorkspaceVisible,
    isSecondaryQuoteEnabled,
    isEssentialWorkspaceVisible,
    isDiscoverPageVisible,
    getIsUserInternal,
  ],
  (landingPage, isPrimaryQuoteEnabled, isSecondaryQuoteEnabled, isDiscoverPageVisible, isUserInternal) => {
    if (!isDefined(landingPage)) {
      return '/loading-user-preferences';
    }
    switch (landingPage) {
      case 'WelcomePage':
        return isDiscoverPageVisible
          ? '/home'
          : getDefaultLandingPage({
              isUserInternal,
              isPrimaryQuoteEnabled,
              isSecondaryQuoteEnabled,
              isDiscoverPageVisible,
            });
      case 'QuoteWorkspace':
        return isPrimaryQuoteEnabled
          ? '/static'
          : getDefaultLandingPage({
              isUserInternal,
              isPrimaryQuoteEnabled,
              isSecondaryQuoteEnabled,
              isDiscoverPageVisible,
            });
      case 'EssentialWorkspace':
        return isPrimaryQuoteEnabled
          ? '/essential'
          : getDefaultLandingPage({
              isUserInternal,
              isPrimaryQuoteEnabled,
              isSecondaryQuoteEnabled,
              isDiscoverPageVisible,
            });
    }
  },
);

function getDefaultLandingPage({
  isUserInternal,
  isPrimaryQuoteEnabled,
  isSecondaryQuoteEnabled,
  isDiscoverPageVisible,
}: {
  isUserInternal: boolean;
  isPrimaryQuoteEnabled: boolean;
  isSecondaryQuoteEnabled: boolean;
  isDiscoverPageVisible: boolean;
}): RoutePath {
  if (isPrimaryQuoteEnabled) {
    return isUserInternal ? '/static' : '/essential';
  }
  if (isDiscoverPageVisible) {
    return '/home';
  }
  if (isSecondaryQuoteEnabled) {
    return '/secondary-market-quotes-workspace';
  }
  return '/underlying-universe';
}

const getDefaultRoutePathOld: (state: AppState) => RoutePath = createSelector(
  [
    getUserPreferencesLandingPage,
    isAdvancedWorkspaceVisible,
    isSecondaryQuoteEnabled,
    isEssentialWorkspaceVisible,
    isDynamicWorkspaceVisible,
    isBatchWorkspaceVisible,
    isPostTradeVisible,
    isPriceImprovementBlotterVisible,
    isScheduledQuotesBlotterVisible,
    isAddonManagementVisible,
  ],
  (
    landingPage,
    isAdvancedWorkspaceVisible,
    isSecondaryQuoteEnabled,
    isEssentialWorkspaceVisible,
    isDynamicWorkspaceVisible,
    isBatchWorkspaceVisible,
    isPostTradeVisible,
    isPriceImprovementBlotterVisible,
    isScheduledQuotesBlotterVisible,
    isAddonManagementVisible,
  ) => {
    if (landingPage === 'WelcomePage') {
      return '/home';
    }
    if (landingPage === 'QuoteWorkspace') {
      if (isAdvancedWorkspaceVisible) {
        return '/static';
      }
      if (isEssentialWorkspaceVisible) {
        return '/essential';
      }
      if (isSecondaryQuoteEnabled) {
        return '/secondary-market-quotes-workspace';
      }
      if (isDynamicWorkspaceVisible) {
        return '/dynamic';
      }
      if (isBatchWorkspaceVisible) {
        return '/batch';
      }
      if (isPostTradeVisible) {
        return '/post-trade';
      }
      if (isPriceImprovementBlotterVisible) {
        return '/price-improvement-blotter';
      }
      if (isScheduledQuotesBlotterVisible) {
        return '/scheduled-quotes-blotter';
      }
      if (isAddonManagementVisible) {
        return '/addon-management';
      }
      return '/underlying-universe';
    }
    return '/loading-user-preferences';
  },
);
